import dayjs from 'dayjs';

import { isEmpty } from '@/utils/isEmpty';
import { isNil } from '@/utils/isNil';
import { isLessThan36Months } from 'src/checkout/templates/Checkout/Forms/CheckoutForm.helpers';
import { FinanceQuoteInfoFormValues } from 'src/checkout/templates/Checkout/Forms/CheckoutForm.types';
import { InputAddressAutocompleteAddress } from 'src/general/components/forms/FormikInputAddressAutocomplete/FormikInputAddressAutocomplete';
import {
  EmploymentCitizenshipFormValues,
  FINANCE_QUOTE_RADIO_TYPE,
  PersonalisedQuotePayloadPartial,
} from 'src/personalised-quote/templates/Forms/PersonalizedQuoteForm.type';

import {
  FINANCE_EMPLOYMENT_STATUS,
  FINANCE_FAMILY_STATUS,
  FINANCE_RESIDENTIAL_STATUS,
  FinanceQuoteRequestPayload,
  RESOLUTION_OF_STATUS_VISA_SUBCLASS,
} from './FinanceApi.types';

const payloadAddressToFinanceAddress = (
  payload: InputAddressAutocompleteAddress,
): FinanceQuoteRequestPayload['applicant']['current_address']['address'] => {
  // Extract the street number from the rest of the street value
  const streetArray = (payload.street?.trim() ?? '').split(' ');
  const streetNumber = streetArray.shift();
  const streetName = streetArray.join(' ');

  return {
    unit: payload.apartment,
    street_number: streetNumber ?? '',
    street_name: streetName,
    suburb: payload.suburb,
    postcode: payload.postcode,
    state: payload.state,
  };
};

export const payloadEmploymentCitizenshipToVisaDetails = (
  employmentCitizenshipPayload: EmploymentCitizenshipFormValues | FinanceQuoteInfoFormValues,
): FinanceQuoteRequestPayload['applicant']['visa'] =>
  employmentCitizenshipPayload.visaSubclassNumber &&
  (employmentCitizenshipPayload.visaExpiryDate ||
    employmentCitizenshipPayload.visaSubclassNumber.toString().trim() === RESOLUTION_OF_STATUS_VISA_SUBCLASS)
    ? {
        code: `${employmentCitizenshipPayload.visaSubclassNumber}`,
        end_date:
          employmentCitizenshipPayload.visaSubclassNumber.toString().trim() === RESOLUTION_OF_STATUS_VISA_SUBCLASS
            ? undefined
            : payloadDateToFinanceEndDate(employmentCitizenshipPayload.visaExpiryDate),
      }
    : undefined;

const isValidDate = (date: Date): boolean => !isNil(date) && dayjs(date).isValid();

const payloadDateToFinanceStartDate = (date: Date | string): string => {
  const currentDate = new Date(date);
  return isValidDate(currentDate) ? dayjs(currentDate).format('YYYY-MM') : '';
};

const payloadDateToFinanceEndDate = (date: Date | string): string => {
  const currentDate = new Date(date);
  return isValidDate(currentDate) ? dayjs(currentDate).format('YYYY-MM-DD') : '';
};

const addInvestmentPropertyDetail = (
  financeInformation: NoUndefined<PersonalisedQuotePayloadPartial['familyDetails']>,
): boolean =>
  !financeInformation.isBusinessUseSelected &&
  [FINANCE_RESIDENTIAL_STATUS.RENTING, FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION].includes(
    financeInformation.residentialStatus,
  ) &&
  financeInformation.hasInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION &&
  financeInformation.hasMortgageOnInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION;

const payloadNumberToFinanceCents = (value: number): number => +`${value}` * 100;

export const personalizedQuotePayloadPartialToFinanceQuoteRequestPayload = (
  payload: PersonalisedQuotePayloadPartial,
): FinanceQuoteRequestPayload => {
  if (
    !payload.loanDetails ||
    !payload.personalDetails ||
    !payload.familyDetails ||
    !payload.employmentAndCitizenshipDetails ||
    !payload.termsAndConditionsDetails ||
    !payload.email ||
    !payload.phone
  ) {
    throw new Error('Missing data in PersonalisedQuotePayloadPartial');
  }

  return {
    is_personalised_quote: true, // flag used to send pubsub message to hubspot-sync from finance-api for personalised quote form
    loan_amount_cents: payloadNumberToFinanceCents(
      payload.loanDetails.depositAmount
        ? payload.loanDetails.estimatedPurchasePrice - payload.loanDetails.depositAmount
        : payload.loanDetails.estimatedPurchasePrice,
    ),
    vehicle: {
      year: `${payload.loanDetails.preferredBuildYear}`,
      make: null,
      model: null,
      price_cents: payloadNumberToFinanceCents(payload.loanDetails.estimatedPurchasePrice),
    },
    applicant: {
      first_name: payload.personalDetails.firstName,
      middle_name: payload.personalDetails.middleName,
      last_name: payload.personalDetails.lastName,
      date_of_birth: payload.personalDetails.dateOfBirth,
      driver_licence: payload.personalDetails.licenceNumber,
      email: payload.email.email,
      phone: payload.phone.phone,
      current_address: {
        address: payloadAddressToFinanceAddress(payload.familyDetails.address as InputAddressAutocompleteAddress),
        start_date: payloadDateToFinanceStartDate(payload.familyDetails.currentAddressStartDate),
      },
      previous_address: isLessThan36Months(payload.familyDetails.currentAddressStartDate)
        ? {
            address: payloadAddressToFinanceAddress(
              payload.familyDetails.previousResidentialAddress as InputAddressAutocompleteAddress,
            ),
            // Set a default start date for the previous address
            start_date: payloadDateToFinanceStartDate(payload.familyDetails.previousAddressStartDate),
          }
        : undefined,
      current_address_status: payload.familyDetails.residentialStatus,
      has_mortgage_on_current_address:
        payload.familyDetails.hasMortgageOnCurrentAddress === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
      has_investment_property: payload.familyDetails.hasInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
      has_mortgage_on_investment_property:
        payload.familyDetails.hasMortgageOnInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
      personal: payload.isBusinessUseSelected
        ? undefined
        : {
            marital_status: payload.familyDetails.familyStatus as FINANCE_FAMILY_STATUS,
            income_cents: payloadNumberToFinanceCents(
              payload.employmentAndCitizenshipDetails.totalYearlyIncomeBeforeTax,
            ),
            has_tenancy_agreement: payload.familyDetails.hasTenancyAgreement === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
            investment_property:
              !isNil(payload.familyDetails.investmentPropertyAddress) &&
              !isEmpty(payload.familyDetails.investmentPropertyAddress) &&
              addInvestmentPropertyDetail(payload.familyDetails)
                ? {
                    address: payloadAddressToFinanceAddress(
                      payload.familyDetails.investmentPropertyAddress as InputAddressAutocompleteAddress,
                    ),
                  }
                : undefined,
            current_employment: {
              status: payload.employmentAndCitizenshipDetails.employmentStatus as FINANCE_EMPLOYMENT_STATUS,
              job_title: payload.employmentAndCitizenshipDetails.jobTitle,
              start_date: payloadDateToFinanceStartDate(payload.employmentAndCitizenshipDetails.startedEmployerDate),
            },
          },
      business: payload.isBusinessUseSelected
        ? {
            abn: payload.personalDetails.abn,
          }
        : undefined,
      residency_status: payload.employmentAndCitizenshipDetails.residencyStatus,
      visa: payloadEmploymentCitizenshipToVisaDetails(payload.employmentAndCitizenshipDetails),
    },
  };
};
